import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import SafeEnterpriseLogService from '../../services/safeEnterpriseLogService';
import CustomDatePicker from '../common/CustomDatePicker';
import SearchableSelect from '../common/SearchableSelect';
import api from '../../services/api';

const ActivityLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    level: '',
    channel: '', 
    component: '',
    user_id: '',
    date_from: '',
    date_to: '',
    search: '',
    correlation_id: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [filterOptions, setFilterOptions] = useState({
    levels: [],
    channels: [],
    categories: [],
    components: []
  });
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [contextModal, setContextModal] = useState({ isOpen: false, context: null, logInfo: null });
  const [auditTrailModal, setAuditTrailModal] = useState({ isOpen: false, auditData: null, loading: false });

  // Calculate statistics from logs data
  const calculateStatsFromLogs = (logsData, totalCount) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const criticalLevels = ['emergency', 'alert', 'critical', 'error'];
    const securityChannels = ['security', 'auth', 'authentication'];
    const securityCategories = ['security', 'authentication', 'login', 'logout'];
    
    const todayLogs = logsData.filter(log => {
      const logDate = new Date(log.timestamp);
      logDate.setHours(0, 0, 0, 0);
      return logDate.getTime() === today.getTime();
    });
    
    const criticalEvents = logsData.filter(log => 
      criticalLevels.includes(log.level?.toLowerCase())
    );
    
     const securityEvents = logsData.filter(log =>
      securityChannels.includes(log.channel?.toLowerCase()) ||
      securityCategories.includes(log.category?.toLowerCase()) ||
      (log.message && (
        log.message.toLowerCase().includes('security') ||
        log.message.toLowerCase().includes('auth') ||
        log.message.toLowerCase().includes('login') ||
        log.message.toLowerCase().includes('logout')
      ))
    );
    
    const calculatedStats = {
      total_logs: totalCount || logsData.length,
      critical_events: criticalEvents.length,
      security_events: securityEvents.length,
      today_logs: todayLogs.length
    };
    
    setStats(calculatedStats);
  };

  // Fetch logs with filters and pagination
  const fetchLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: pagination.limit,
        ...filters
      };
      const data = await SafeEnterpriseLogService.getLogs(params);
      if (data.status === 1) {
        const logsData = data.data.data || [];
        setLogs(logsData);
        setPagination(prev => ({
          ...prev,
          page: data.data.meta?.current_page || 1,
          total: data.data.meta?.total || 0,
          totalPages: data.data.meta?.last_page || 1
        }));
        // Calculate stats from logs data if not already set
        if (!stats || Object.values(stats).every(val => val === 0)) {
          calculateStatsFromLogs(logsData, data.data.meta?.total || 0);
        }
      } else {
        // Set error and clear logs on failure
        setLogs([]);
        setError(data.message || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const data = await SafeEnterpriseLogService.getFilterOptions();

      if (data.status === 1) {
        setFilterOptions({
          levels: Array.isArray(data.data.levels) ? data.data.levels : [],
          channels: Array.isArray(data.data.channels) ? data.data.channels : [],
          categories: Array.isArray(data.data.categories) ? data.data.categories : [],
          components: Array.isArray(data.data.components) ? data.data.components : []
        });
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setFilterOptions({
        levels: [],
        channels: [],
        categories: [],
        components: []
      });
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const data = await SafeEnterpriseLogService.getStatistics();
      if (data.status === 1) {
        // Use overview for correct stats values
        const overview = data.data?.overview || {};
        const statsData = {
          total_logs: overview.total_logs || 0,
          critical_events: overview.total_critical || overview.critical_events || 0,
          security_events: overview.total_security || overview.security_events || 0,
          today_logs: overview.today_activity || overview.today_logs || 0,
          ...overview,
          ...data.data // Include any additional properties
        };
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Set default stats on error
      setStats({
        total_logs: 0,
        critical_events: 0,
        security_events: 0,
        today_logs: 0
      });
    }
  };

  // Fetch users for searchable select
  const fetchUsers = async () => {
    try {
      const allUsersData = await api.get('/api/users?pageSize=5000&limit=5000');

      if (allUsersData?.data) {
        let usersList = [];
        
        if (allUsersData.data.status === 1) {
          if (allUsersData.data.data && Array.isArray(allUsersData.data.data.rows)) {
            usersList = allUsersData.data.data.rows;
          } else if (allUsersData.data.data && Array.isArray(allUsersData.data.data)) {
            usersList = allUsersData.data.data;
          } else if (allUsersData.data.users && Array.isArray(allUsersData.data.users)) {
            usersList = allUsersData.data.users;
          }
        } else if (Array.isArray(allUsersData.data)) {
          usersList = allUsersData.data;
        }
        
        const userOptions = usersList.map(u => ({
          value: u.user_id,
          label: u.user_full_name || u.user_email || 'Unknown User',
          name: u.user_full_name || u.user_email || 'Unknown User',
          email: u.user_email,
          fullLabel: `${u.user_full_name || u.user_email || 'Unknown'} (${u.user_email || u.user_id})`
        }));
        setUsers(userOptions);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch suppliers for searchable select when channel is supplier
  const fetchSuppliers = async () => {
    try {
      // Assuming there's a suppliers API endpoint
      const response = await fetch('/api/suppliers');
      const data = await response.json();
      
      if (data.status === 1 && data.data) {
        const supplierOptions = data.data.map(supplier => ({
          value: supplier.id || supplier.supplier_id,
          label: supplier.supplier_name || supplier.company_name || 'Unknown Supplier',
          name: supplier.supplier_name || supplier.company_name || 'Unknown Supplier',
          email: supplier.supplier_email,
          fullLabel: `${supplier.supplier_name || supplier.company_name || 'Unknown'} (${supplier.supplier_email || ''})`
        }));
        setSuppliers(supplierOptions);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      // Fallback: try to get supplier info from logs
      try {
        const logsResponse = await SafeEnterpriseLogService.getLogs({ channel: 'supplier', limit: 100 });
        if (logsResponse.status === 1 && logsResponse.data.data) {
          const uniqueSuppliers = new Map();
          
          logsResponse.data.data.forEach(log => {
            if (log.supplier_info?.supplier_name || log.supplier_info?.supplier_email) {
              const supplierId = log.supplier_info.supplier_id || log.user_id || log.supplier_info.supplier_email;
              if (!uniqueSuppliers.has(supplierId)) {
                uniqueSuppliers.set(supplierId, {
                  value: supplierId,
                  label: log.supplier_info.supplier_name || 'Unknown Supplier',
                  name: log.supplier_info.supplier_name || 'Unknown Supplier',
                  email: log.supplier_info.supplier_email,
                  fullLabel: `${log.supplier_info.supplier_name || 'Unknown'} (${log.supplier_info.supplier_email || ''})`
                });
              }
            }
          });
          
          setSuppliers(Array.from(uniqueSuppliers.values()));
        }
      } catch (fallbackErr) {
        console.error('Error fetching suppliers from logs:', fallbackErr);
      }
    }
  };

  // Fetch all logs for accurate statistics calculation
  const fetchAllLogsForStats = async () => {
    try {
      const params = {
        page: '1',
        limit: '1000', // Large limit to get more representative data
        ...filters
      };

      const data = await SafeEnterpriseLogService.getLogs(params);
      
      if (data.status === 1) {
        const logsData = data.data.data || [];
        calculateStatsFromLogs(logsData, data.data.meta?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching logs for stats calculation:', err);
    }
  };

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && user.user_id) {
      fetchFilterOptions();
      fetchStats();
      fetchUsers();
      // Fallback to calculate stats from logs data
      setTimeout(() => fetchAllLogsForStats(), 2000);
    }
  }, [user]);

  useEffect(() => {
    // Only fetch logs if user is authenticated and we have filters
    if (user && user.user_id) {
      fetchLogs(1);
    }
  }, [filters, user]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    
    // When channel changes to 'supplier', fetch suppliers for user filter
    if (key === 'channel') {
      if (value === 'supplier' || value === 'suppliers') {
        // Clear current user selection when switching to supplier channel
        setSelectedUser(null);
        newFilters.user_id = '';
        // Fetch suppliers if not already loaded
        if (suppliers.length === 0) {
          fetchSuppliers();
        }
      } else if (value !== 'supplier' && value !== 'suppliers') {
        // Clear user selection when switching away from supplier channel
        setSelectedUser(null);
        newFilters.user_id = '';
      }
    }
    
    setFilters(newFilters);
  };

  const handleUserSelect = (selectedUser) => {
    setSelectedUser(selectedUser);
    handleFilterChange('user_id', selectedUser ? selectedUser.value : '');
  };

  const clearFilters = () => {
    setFilters({
      level: '',
      channel: '', 
      component: '',
      user_id: '',
      date_from: '',
      date_to: '',
      search: '',
      correlation_id: ''
    });
    setSelectedUser(null);
  };

  // Check if a log entry has the required information for audit trail
  const canShowAuditTrail = (log) => {
    // Check if message has entity format (e.g., "Entity create: Quotation #123")
    const messageMatch = log.message?.match(/Entity\s+(create|update|delete):\s+(\w+)\s+#(\d+)/i);
    if (messageMatch) {
      return true;
    }
    
    // Check if context has entity information
    try {
      const context = typeof log.context === 'string' ? JSON.parse(log.context) : log.context;
      if (context?.entity_type && context?.entity_id) {
        return true;
      }
      if (context?.specification_id && (context?.entity_type === 'Specification' || log.message?.includes('Specification'))) {
        return true;
      }
    } catch (e) {
      // Context parsing failed
    }
    
    return false;
  };

  // Fetch detailed audit trail for a specific log entry
  const fetchAuditTrail = async (log) => {
    setAuditTrailModal({ isOpen: true, auditData: null, loading: true });
    
    try {
      // Extract entity information from the log context or message
      let entityType = null;
      let entityId = null;
      
      // Try to parse from message (e.g., "Entity create: Quotation #123" or "Entity update: Specification #4414")
      const messageMatch = log.message?.match(/Entity\s+(create|update|delete):\s+(\w+)\s+#(\d+)/i);
      if (messageMatch) {
        entityType = messageMatch[2];
        entityId = messageMatch[3];
      }
      
      // Try to parse from context
      if (!entityType || !entityId) {
        try {
          const context = typeof log.context === 'string' ? JSON.parse(log.context) : log.context;
          if (context?.entity_type) entityType = context.entity_type;
          if (context?.entity_id) entityId = context.entity_id;
          if (context?.specification_id && !entityId) {
            entityType = 'Specification';
            entityId = context.specification_id;
          }
        } catch (e) {
          console.warn('Could not parse context for audit trail');
        }
      }
      
      if (!entityType || !entityId) {
        throw new Error('Cannot determine entity type and ID for audit trail');
      }
      
      const params = {
        entity_type: entityType,
        entity_id: entityId,
        limit: 50,
        // Get all actions to see both creation and updates
        action: '' // Don't filter by action to see the complete history
      };
      
      const response = await SafeEnterpriseLogService.getAuditTrail(params);
      
      if (response.status === 1) {
        setAuditTrailModal({ 
          isOpen: true, 
          auditData: {
            entityType,
            entityId,
            changes: response.data.data || []
          }, 
          loading: false 
        });
      } else {
        throw new Error(response.message || 'Failed to fetch audit trail');
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      setAuditTrailModal({ 
        isOpen: true, 
        auditData: { error: error.message }, 
        loading: false 
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getLevelColor = (level) => {
    return SafeEnterpriseLogService.getLevelColor(level);
  };

  const formatContext = (context, logInfo = null) => {
    if (!context) return null;
    
    try {
      const parsed = SafeEnterpriseLogService.formatContext(context);
      
      // Prioritize important supplier context information
      const priorityKeys = ['business_process', 'endpoint', 'supplier_email', 'supplier_name', 'company_name', 'compliance_level', 'security_event'];
      const importantEntries = [];
      const otherEntries = [];
      
      Object.entries(parsed).forEach(([key, value]) => {
        if (priorityKeys.includes(key)) {
          importantEntries.push([key, value]);
        } else {
          otherEntries.push([key, value]);
        }
      });
      
      const displayEntries = [...importantEntries, ...otherEntries];
      const totalItems = displayEntries.length;
      
      return (
        <div className="text-xs text-gray-600 max-w-xs">
          {displayEntries.slice(0, 3).map(([key, value]) => (
            <div key={key} className="truncate">
              <span className={`font-medium ${priorityKeys.includes(key) ? 'text-blue-600' : ''}`}>
                {key}:
              </span>{' '}
              <span className={priorityKeys.includes(key) ? 'text-blue-800' : ''}>
                {String(value)}
              </span>
            </div>
          ))}
          {totalItems > 3 && (
            <button
              onClick={() => setContextModal({ isOpen: true, context: parsed, logInfo })}
              className="text-blue-500 hover:text-blue-700 cursor-pointer underline"
            >
              ... and {totalItems - 3} more
            </button>
          )}
        </div>
      );
    } catch (err) {
      return <span className="text-xs text-gray-500">Invalid context</span>;
    }
  };

  // Context Modal Component
  const ContextModal = () => {
    if (!contextModal.isOpen || !contextModal.context) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Log Context Details</h3>
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, logInfo: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Log Info */}
            {contextModal.logInfo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600">
                  <strong>Timestamp:</strong> {formatDate(contextModal.logInfo.timestamp)}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>Message:</strong> {contextModal.logInfo.message}
                </div>
              </div>
            )}

            {/* Context Data */}
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {Object.entries(contextModal.context).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-100 pb-2">
                    <div className="flex flex-col sm:flex-row">
                      <div className="w-full sm:w-1/3 font-medium text-gray-900 text-sm">
                        {key}:
                      </div>
                      <div className="w-full sm:w-2/3 text-sm text-gray-700 break-all">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, logInfo: null })}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Audit Trail Modal Component
  const AuditTrailModal = () => {
    if (!auditTrailModal.isOpen) return null;

    const { auditData, loading } = auditTrailModal;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Detailed Audit Trail
                {auditData?.entityType && auditData?.entityId && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({auditData.entityType} #{auditData.entityId})
                  </span>
                )}
              </h3>
              <button
                onClick={() => setAuditTrailModal({ isOpen: false, auditData: null, loading: false })}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading detailed changes...</span>
                </div>
              ) : auditData?.error ? (
                <div className="text-center py-8">
                  <div className="text-red-600 mb-2">⚠ Error Loading Audit Trail</div>
                  <div className="text-sm text-gray-600">{auditData.error}</div>
                </div>
              ) : auditData?.changes?.length > 0 ? (
                <div className="space-y-4">
                  {auditData.changes.map((change, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatDate(change.timestamp)}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              change.action === 'create' ? 'bg-green-100 text-green-800' :
                              change.action === 'update' ? 'bg-blue-100 text-blue-800' :
                              change.action === 'delete' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {change.action ? change.action.toUpperCase() : 'UPDATE'}
                            </span>
                            {change.action === 'create' && (
                              <span className="text-xs text-green-600 font-medium">
                                ✓ Initial Creation
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {change.user_name && (
                            <div className="flex items-center gap-2 justify-end">
                              <div className="text-sm text-gray-600">{change.user_name}</div>
                              {(() => {
                                // Determine user type and return appropriate badge
                                let userType = 'Internal User';
                                let badgeColor = 'bg-green-100 text-green-800';
                                
                                if (change.user_info?.user_role) {
                                  userType = change.user_info.user_role;
                                } else if (change.user_name === 'System') {
                                  // Check if this is a user action vs true system operation
                                  const isUserAction = (change.action === 'update' || change.action === 'create') && 
                                                      (change.old_values || change.new_values);
                                  if (isUserAction) {
                                    userType = 'Internal User';
                                    badgeColor = 'bg-green-100 text-green-800';
                                  } else {
                                    userType = 'System';
                                    badgeColor = 'bg-gray-100 text-gray-800';
                                  }
                                } else if (change.supplier_info?.is_supplier_context) {
                                  userType = 'Supplier';
                                  badgeColor = 'bg-blue-100 text-blue-800';
                                } else if (change.user_info?.is_user_context) {
                                  userType = 'Internal';
                                  badgeColor = 'bg-green-100 text-green-800';
                                } else if (change.user_type && change.user_type !== 'unknown') {
                                  userType = change.user_type === 'supplier' ? 'Supplier' : 'Internal';
                                  badgeColor = change.user_type === 'supplier' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
                                }
                                
                                return (
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${badgeColor}`}>
                                    {userType}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {(() => {
                              // Additional context information
                              if (change.user_info?.user_role) {
                                return change.user_info.user_role;
                              }
                              
                              if (change.user_name === 'System') {
                                // Check if this looks like a user-initiated action
                                const isUserAction = (change.action === 'update' || change.action === 'create') && 
                                                    (change.old_values || change.new_values);
                                return isUserAction ? 'Internal Portal Action' : 'System Operation';
                              }
                              
                              if (change.supplier_info?.is_supplier_context) {
                                return 'Supplier Portal Action';
                              }
                              
                              if (change.user_info?.is_user_context) {
                                return 'Internal Portal Action';
                              }
                              
                              return 'Portal Operation';
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Changes Summary */}
                      {change.changes_summary && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-gray-700 mb-1">Summary:</div>
                          <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                            {change.changes_summary}
                          </div>
                        </div>
                      )}

                      {/* Field Changes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Old Values */}
                        <div>
                          <div className="text-xs font-medium text-red-700 mb-2">Old Values:</div>
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            {change.old_values === null ? (
                              <div className="text-xs text-gray-500 italic">
                                null (This was a creation - no previous values existed)
                              </div>
                            ) : change.old_values ? (
                              typeof change.old_values === 'string' ? (
                                <pre className="text-xs text-red-800 whitespace-pre-wrap">
                                  {change.old_values}
                                </pre>
                              ) : (
                                <pre className="text-xs text-red-800 whitespace-pre-wrap">
                                  {JSON.stringify(change.old_values, null, 2)}
                                </pre>
                              )
                            ) : (
                              <div className="text-xs text-gray-500 italic">
                                No old values (empty)
                              </div>
                            )}
                          </div>
                        </div>

                        {/* New Values */}
                        {change.new_values && (
                          <div>
                            <div className="text-xs font-medium text-green-700 mb-2">New Values:</div>
                            <div className="bg-green-50 border border-green-200 rounded p-2">
                              {typeof change.new_values === 'string' ? (
                                <pre className="text-xs text-green-800 whitespace-pre-wrap">
                                  {change.new_values}
                                </pre>
                              ) : (
                                <pre className="text-xs text-green-800 whitespace-pre-wrap">
                                  {JSON.stringify(change.new_values, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-2">📋 No Detailed Changes Found</div>
                  <div className="text-sm text-gray-400">
                    This log entry may not have detailed field-level audit trail data.
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setAuditTrailModal({ isOpen: false, auditData: null, loading: false })}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  Activity Logs
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Comprehensive audit trails and user activity monitoring
                </p>
                {/* Activity Health Indicators */}
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">User Activities</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">Audit Trails</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">Data Changes</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Statistics */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">
                  {stats ? (stats.total_logs || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Total Logs</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-red-600">
                  {stats ? (stats.critical_events || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Critical Events</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-purple-600">
                  {stats ? (stats.security_events || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Security Events</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-orange-600">
                  {stats ? (stats.today_logs || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Today's Events</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Filters</h2>
            
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Levels</option>
                  {filterOptions.levels.map((level) => (
                    <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <select
                  value={filters.channel}
                  onChange={(e) => handleFilterChange('channel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Channels</option>
                  {filterOptions.channels.map((channel) => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))}
                </select>
              </div>
 
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Component</label>
                <select
                  value={filters.component}
                  onChange={(e) => handleFilterChange('component', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Components</option>
                  {filterOptions.components.map((component) => (
                    <option key={component} value={component}>{component}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {filters.channel === 'supplier' || filters.channel === 'suppliers' ? 'Supplier' : 'User'}
                </label>
                <SearchableSelect
                  items={filters.channel === 'supplier' || filters.channel === 'suppliers' ? suppliers : users}
                  onSelectItem={handleUserSelect}
                  initialSelectedItem={selectedUser}
                  placeholder={filters.channel === 'supplier' || filters.channel === 'suppliers' ? 'Select supplier...' : 'Select user...'}
                />
              </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <CustomDatePicker
                  value={filters.date_from}
                  onChange={(date) => handleFilterChange('date_from', date)}
                  placeholder="Select from date"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <CustomDatePicker
                  value={filters.date_to}
                  onChange={(date) => handleFilterChange('date_to', date)}
                  placeholder="Select to date"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search messages, contexts..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correlation ID</label>
                <input
                  type="text"
                  value={filters.correlation_id}
                  onChange={(e) => handleFilterChange('correlation_id', e.target.value)}
                  placeholder="Track requests..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Enterprise Log Entries</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading logs...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              <p>Error loading logs: {error}</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No logs found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Channel
                      </th> 
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Message & Context
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(log.level)}`}>
                            {log.level ? log.level.charAt(0).toUpperCase() + log.level.slice(1) : 'Info'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs rounded-full">
                            {log.channel || 'system'}
                          </span>
                        </td> 
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-lg">
                          <div className="mb-1">
                            <span className="font-medium">{log.message || 'No message'}</span>
                          </div>
                     
                          {/* Context information */}
                          <div className="text-xs text-gray-500">
                            {formatContext(log.context, log)}
                          </div>
                          {log.correlation_id && (
                            <div className="text-xs text-gray-400 mt-1">
                              ID: {log.correlation_id.substring(0, 8)}...
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {/* Show supplier info if it's a supplier context */}
                          {log.supplier_info?.is_supplier_context ? (
                            <div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Supplier
                              </span>
                              {/* If there's an email, this is an individual user */}
                              {log.supplier_info.supplier_email ? (
                                <div>
                                  <div className="text-xs text-gray-600 mt-1">{log.supplier_info.supplier_name}</div>
                                  <div className="text-xs text-gray-400 mt-1">{log.supplier_info.supplier_email}</div>
                                </div>
                              ) : (
                                /* No email means this is a company name */
                                <div>
                                  <div className="text-xs text-gray-500 mt-1">{log.supplier_info.supplier_name}</div>
                                  <div className="text-xs text-gray-400 mt-1">Company</div>
                                </div>
                              )}
                            </div>
                          ) : log.user_info?.is_user_context ? (
                            /* Show internal user info */
                            <div>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                User
                              </span>
                              {log.user_info.user_name && (
                                <div className="text-xs text-gray-600 mt-1">{log.user_info.user_name}</div>
                              )}
                              {log.user_info.user_role && (
                                <div className="text-xs text-gray-400 mt-1">{log.user_info.user_role}</div>
                              )}
                              {log.user_info.user_email && (
                                <div className="text-xs text-gray-400 mt-1">{log.user_info.user_email}</div>
                              )}
                            </div>
                          ) : (
                            <div>
                              {log.user_type === 'supplier' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Supplier
                                </span>
                              ) : (
                                <span>{log.user_id || 'System'}</span>
                              )}
                              {log.user_name && (
                                <div className="text-xs text-gray-500">{log.user_name}</div>
                              )}
                              {log.user_type && log.user_type !== 'unknown' && log.user_type !== 'supplier' && (
                                <div className="text-xs text-gray-400">{log.user_type}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {/* Show audit trail button only if log has proper entity information */}
                          {canShowAuditTrail(log) && (
                            <button
                              onClick={() => fetchAuditTrail(log)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="View detailed field changes"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Details
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => fetchLogs(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchLogs(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => fetchLogs(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => fetchLogs(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pagination.page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => fetchLogs(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Context Modal */}
        <ContextModal />

        {/* Audit Trail Modal */}
        <AuditTrailModal />
      </div>
    </div>
  );
};

export default ActivityLogs;